import { action, computed, observable } from 'mobx';
import { ProjectsRootVisualStore } from '../../common/stores';
import { UploadedPackagesVisualStore } from '.';
import { ProjectsService } from '../../common/services';
import type { FeatureFlagProcessingPlan, PackageFeatureFlagsPreview } from '../models/FeatureFlagProcessingPlan';
import { showDocumentProcessingResults } from '../../common/components/DocumentProcessingResults';
import { createFeatureFlagsPlanning } from '../models/FeatureFlagsPlanning';
import { documentProcessingFailureOutcomes } from '../../common/misc/documentProcessingOutcomes';

export default class UpdatePackagesFeatureFlagsStore {
    @observable
    modalVisible: boolean = false;

    readonly featureFlagsPlanning = createFeatureFlagsPlanning(
        (packageIds: string[], flags: Record<string, string> | null) =>
            this.service.previewPackagesFeatureFlags(packageIds, flags)
    );

    private modalRevision = 0;

    constructor(
        private readonly projectsStore: ProjectsRootVisualStore,
        private readonly packagesStore: UploadedPackagesVisualStore,
        private readonly service: ProjectsService
    ) {}

    @action
    setModalVisible(modalVisible: boolean) {
        this.modalRevision++;
        this.modalVisible = modalVisible;
    }

    @action
    async updatePackagesFeatureFlags(
        featureFlags: Record<string, string> | null,
        previews: PackageFeatureFlagsPreview[],
        packageIds: string[]
    ) {
        const projectId = this.project?.id;
        const modalRevision = this.modalRevision;
        const plans: FeatureFlagProcessingPlan[] = [];
        for (const { plan } of previews) {
            if (plan) {
                plans.push(plan);
            }
        }
        const response = await this.service.savePackagesFeatureFlagsAndRun(packageIds, featureFlags, plans);
        if (response.isErr()) {
            const fileNames = new Map(previews.map(choice => [choice.packageId, choice.fileName]));
            showDocumentProcessingResults(
                documentProcessingFailureOutcomes(
                    packageIds.map(packageId => ({
                        packageId,
                        fileName: fileNames.get(packageId) || packageId
                    })),
                    response.error
                )
            );
            return false;
        }
        showDocumentProcessingResults(response.value);
        const allStarted =
            response.value.length === packageIds.length && response.value.every(result => result.status === 'Started');
        if (this.project?.id === projectId) {
            await this.packagesStore.loadProjectPackages(this.packagesStore.searchTerm);
            if (
                allStarted &&
                this.project?.id === projectId &&
                this.modalRevision === modalRevision &&
                JSON.stringify(this.packagesToUpdate) === JSON.stringify(packageIds)
            ) {
                this.packagesStore.resetPackagesSelection();
                this.setModalVisible(false);
            }
        }
        return allStarted;
    }

    @computed
    get project() {
        return this.projectsStore.currentProject;
    }

    @computed
    get projectFeatureFlags() {
        return this.packagesStore.currentProjectFeatureFlags;
    }

    @computed
    get packagesToUpdate() {
        return this.packagesStore.selectedPackages.filter(id => !this.packagesStore.protectedPackageIds.includes(id));
    }

    @computed
    get menuOptionVisible() {
        return this.packagesToUpdate.length > 0;
    }
}
